import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import MainLayout from '@shared/ui/main-layout';
import {
  resetContacts,
  fetchAllContacts,
  deleteContact,
  deleteAppContact,
} from '../../entities/contact/model';
import {
  resetCurrentLegalEntity,
  resetLegalEntities,
  fetchAllLegalEntities,
  deleteLegalEntity,
  deleteAppLegalEntity,
} from '../../entities/legal-entity/model';
import { useAppDispatch, useAppSelector } from '../../app/store/store';
import {
  Contact,
  CompanyRequestInvite,
  LegalEntity,
  User,
  Employee,
} from '../../types/interfaces';
import { AbilityContext, Can } from '@shared/lib/ability';
import { AddEntity } from '../../shared/ui/add-entity';
import { Tabs, Tab } from '../../shared/ui/tabs';
import {
  resetCurrentEmployee,
  selectEmployees,
  selectEmployeesStatus,
  fetchAllEmployees,
  deleteEmployee,
} from '../../entities/employee/model';
import { useMe } from '../Area/Area';
import { fetchCompanyById } from '../../entities/company/model';
import { fetchPendingJoinRequestsCount } from '../../entities/joinrequest/model';
import CompanyAccessRequest from '@features/company-access-request';
import { apiClient } from '../../api/instance';
import {
  setNotification,
  setErrorNotification,
} from '../../app/store/notifications';
import DataTable, { Column } from '@shared/ui/data-table';
import Avatar from '@shared/ui/avatar';
import { Button } from '@shared/ui/button';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import { DEFAULT_PAGE_SIZE } from '@shared/config/pagination';
import { MyModal } from '@shared/ui/my-modal';
import { rolesApi, InviteWithMeta } from '../../api/services/roles.service';
import {
  INVITE_STATUS_STYLE,
  buildInviteLink,
  copyToClipboard,
} from '@entities/invite/lib/invite-link';
import { getTaxLabelForCompany } from '@shared/lib/tax-label';

const Company = () => {
  const { me }: { me: User } = useMe();
  const { companyId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    if (companyId) return;
    if (me?.company?.id) {
      navigate(`/company/${me.company.id}`, { replace: true });
      return;
    }
    // Нет id и нет своей компании. Менеджер/админ → к списку компаний.
    const MANAGER_OR_ADMIN_GROUPS = [
      'MANAGER',
      'OFFICE_MANAGER',
      'SALES_DIRECTOR',
      'admins',
      'HD_HEAD',
      'HD_ENGINEER',
      'HD_SERVICE_ENGINEER',
    ];
    const isManagerOrAdmin =
      !!me &&
      (me.is_staff ||
        me.is_superuser ||
        (me.groups ?? []).some((g) => MANAGER_OR_ADMIN_GROUPS.includes(g)));
    if (isManagerOrAdmin) {
      navigate('/companies', { replace: true });
    }
  }, [companyId, me, navigate]);

  const employees = useAppSelector(selectEmployees);
  const legalEntities = useAppSelector(
    (state) => state.legalEntities.legalEntities
  );
  const contacts = useAppSelector((state) => state.contacts.contacts);

  const filteredEmployees = useMemo(
    () =>
      employees.filter(
        (e) => e.company?.id === Number(companyId) && !e.groups?.includes('BASIC')
      ),
    [employees, companyId]
  );
  const ability = useContext(AbilityContext);


  const canKickEmployees = useMemo(() => {
    if (!me) return false;
    if (me.is_staff || me.is_superuser) return true;
    if ((me.groups ?? []).includes('COMPANY_OWNER')) return true;
    // edit_username permission — исторический сценарий MANAGER'ов;
    // на фронте маппится в CASL как 'editemployee' on 'username'.
    return ability.can('editemployee', 'username');
  }, [me, ability]);

  const employeesStatus = useAppSelector(selectEmployeesStatus);
  const legalEntitiesStatus = useAppSelector(
    (state) => state.legalEntities.listFetchStatus
  );
  const contactsStatus = useAppSelector(
    (state) => state.contacts.listFetchStatus
  );
  const dispatch = useAppDispatch();
  const company = useAppSelector((state) => state.companies.currentCompany);
  const companyStatus = useAppSelector((state) => state.companies.status);
  const shouldShowNoCompany =
    !companyId &&
    !me?.company &&
    (me?.groups ?? []).includes('BASIC') &&
    !(me?.groups ?? []).some((g) =>
      [
        'MANAGER',
        'OFFICE_MANAGER',
        'SALES_DIRECTOR',
        'admins',
        'HD_HEAD',
        'HD_ENGINEER',
        'HD_SERVICE_ENGINEER',
      ].includes(g)
    );

  const [basicGroupInvites, setBasicGroupInvites] = useState<
    CompanyRequestInvite[]
  >([]);
 
  const [myJoinRequestsRefreshKey, setMyJoinRequestsRefreshKey] =
    useState(0);
  const [employeeModal, setEmployeeModal] = useState<Employee | null>(null);
  const [contactModal, setContactModal] = useState<Contact | null>(null);

  const [legalEntityModal, setLegalEntityModal] = useState<LegalEntity | null>(
    null
  );


  const [companyInvite, setCompanyInvite] = useState<InviteWithMeta | null>(
    null
  );
  const [companyInviteLoading, setCompanyInviteLoading] = useState(false);

  const fetchCompanyInvite = async () => {
    if (!companyId) {
      setCompanyInvite(null);
      return;
    }
    setCompanyInviteLoading(true);
    try {
    
      const res = await rolesApi.getInvites({ type: 'company' });
      const own = res.data.filter(
        (inv) => inv.company_id === Number(companyId)
      );
    
      const order = { unused: 0, used: 1, blocked: 2 } as const;
      own.sort((a, b) => {
        const oa = order[a.status as keyof typeof order] ?? 3;
        const ob = order[b.status as keyof typeof order] ?? 3;
        if (oa !== ob) return oa - ob;
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });
      setCompanyInvite(own[0] || null);
    } catch (error) {
      console.error('Ошибка при загрузке инвайта компании:', error);
      setCompanyInvite(null);
    } finally {
      setCompanyInviteLoading(false);
    }
  };

  const fetchBasicGroupInvites = async () => {
    if (!companyId) {
      setBasicGroupInvites([]);
      return;
    }
    try {
      const response = await apiClient.get('/joinrequests/', {
        params: { company: companyId, status: 'pending', count: 100 },
      });
      setBasicGroupInvites(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке запросов на присоединение:', error);
    }
  };

  useEffect(() => {
    fetchBasicGroupInvites();
    fetchCompanyInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (employeesStatus === 'idle') dispatch(fetchAllEmployees());
  }, [dispatch, employeesStatus]);

  // При смене companyId сразу подчищаем стейл и заново грузим
  // юр. лица / контакты конкретно этой компании. Без сброса предыдущие
  // записи могли отображаться, пока ответ нового запроса не пришёл,
  // или вообще, если статус был 'succeeded' и мы решили «уже грузили».
  useEffect(() => {
    if (!companyId) return;
    dispatch(resetLegalEntities());
    dispatch(fetchAllLegalEntities({ company: companyId }));
  }, [dispatch, companyId]);

  useEffect(() => {
    if (!companyId) return;
    dispatch(resetContacts());
    dispatch(fetchAllContacts({ company: companyId }));
  }, [dispatch, companyId]);

  useEffect(() => {
    if (companyId) {
      dispatch(fetchCompanyById(+companyId));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    dispatch(resetCurrentEmployee());
    return () => {
      // Сбрасываем только «текущее юр. лицо» — это вспомогательный
      // буфер для формы редактирования. Списки юр. лиц / контактов
      // уже подменяются `companyId`-эффектом ниже, и резетить их на
      // unmount вредно: в dev (StrictMode) cleanup отрабатывает сразу
      // после первого mount, и данные, загруженные ранее Dashboard'ом
      // или /me, стираются раньше, чем успеет уйти запрос.
      dispatch(resetCurrentLegalEntity());
    };
  }, [dispatch]);

  const location = useLocation();
  const activeTab = Number(location.search.slice(12));

  // ─── Колонки таблиц ──────────────────────────────────────────────

  const fullName = (u: { first_name?: string; last_name?: string; username?: string }) =>
    [u.last_name, u.first_name].filter(Boolean).join(' ') || u.username || '—';

  const employeeRoleLabel = (e: Employee): string => {
    const groups = (e as Employee & { groups?: string[] }).groups || [];
    if (e.is_superuser) return 'Администратор';
    if (groups.some((g) => g === 'COMPANY_OWNER' || g === 'OWNER')) {
      return 'Владелец компании';
    }
    const known: Record<string, string> = {
      USER: 'Сотрудник компании',
      MANAGER: 'Менеджер',
      HD_HEAD: 'Руководитель ТП',
      HD_ENGINEER: 'Инженер ТП',
      HD_SERVICE_ENGINEER: 'Сервис. инженер',
      BASIC: '—',
    };
    for (const g of groups) {
      if (known[g]) return known[g];
    }
    return 'Сотрудник';
  };

  const legalEntityColumns: Column<LegalEntity>[] = [
    {
      key: 'name',
      header: 'Юр. лицо',
      render: (le) => (
        <div className="min-w-0">
          <p className="font-medium text-[#2d2c2c] truncate" title={le.name}>
            {le.name}
          </p>
          {le.addresses?.[0] ? (
            <p className="text-xs text-gray-400 truncate max-w-[280px]">
              {Object.values(le.addresses[0]).filter(Boolean).join(', ')}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'inn',
      // У одной компании страна юр.лиц одинаковая (см. бэк-логику
      // RegisterCompanySerializer.create), поэтому шапка колонки
      // безопасно подбирается через первое юр.лицо.
      header: getTaxLabelForCompany(company),
      render: (le) => (
        <span className="text-gray-600 font-mono text-xs">
          {le.inn || '—'}
        </span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (le) =>
        le.email ? (
          <a
            href={`mailto:${le.email}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs"
          >
            {le.email}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'phone',
      header: 'Телефон',
      render: (le) => {
        const phone = le.phones?.[0];
        return phone ? (
          <a
            href={`tel:${phone}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs"
          >
            {phone}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      headerClassName: 'w-[1%]',
      className: 'text-right whitespace-nowrap',
      render: (le) => (
        <div className="flex items-center justify-end gap-3">
          <Can I="change" a="legalentity">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`legal-entities/${le.id}`);
              }}
              className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
            >
              Изменить
            </button>
            <ConfirmModal
              openButton={
                <button
                  type="button"
                  className="text-sm font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
                >
                  Удалить
                </button>
              }
              confirm={() => {
                dispatch(deleteLegalEntity(le.id));
                dispatch(deleteAppLegalEntity(String(le.id)));
              }}
              texts={{
                title: 'Удаление',
                text: 'Удалить это юр. лицо?',
                cancel: 'Отмена',
                confirm: 'Удалить',
              }}
            />
          </Can>
        </div>
      ),
    },
  ];

  const employeeColumns: Column<Employee>[] = [
    {
      key: 'who',
      header: 'Сотрудник',
      render: (e) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            src={e.photo}
            name={fullName(e)}
            size={36}
            kind="person"
          />
          <div className="min-w-0">
            <p className="font-medium text-[#2d2c2c] truncate">
              {fullName(e)}
            </p>
            <p className="text-xs text-gray-400">@{e.username}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (e) =>
        e.email ? (
          <a
            href={`mailto:${e.email}`}
            onClick={(ev) => ev.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs break-all"
          >
            {e.email}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'role',
      header: 'Роль',
      render: (e) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
          {employeeRoleLabel(e)}
        </span>
      ),
    },
    {
      key: 'phone',
      header: 'Телефон',
      render: (e) =>
        e.mobile_phone ? (
          <a
            href={`tel:${e.mobile_phone}`}
            onClick={(ev) => ev.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs"
          >
            {e.mobile_phone}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      className: 'text-right whitespace-nowrap',
      render: (e) => {
        // Сам себя удалять/редактировать через эту строку нельзя.
        // Бэк всё равно вернёт 403 на DELETE /employees/{me_id}/
        // (см. EmployeesViewSet.destroy), но скрывать кнопку у себя
        // в строке — лучший UX, чем красное уведомление.
        const isSelf = me?.id === e.id;
        return (
          <div className="flex items-center justify-end gap-3">
            <Can I="editemployee" a="username">
              <button
                type="button"
                onClick={(ev) => {
                  ev.stopPropagation();
                  navigate(`employee/${e.id}`);
                }}
                className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
              >
                Изменить
              </button>
            </Can>
            {canKickEmployees && !isSelf && (
              <ConfirmModal
                openButton={
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
                  >
                    Удалить
                  </button>
                }
                confirm={async () => {
                  try {
                    await dispatch(
                      deleteEmployee(String(e.id))
                    ).unwrap();
                    dispatch(
                      setNotification(
                        `${fullName(e)} удалён(а) из компании`
                      )
                    );
                  } catch (err: any) {
                    const message =
                      err?.response?.data?.detail ||
                      err?.response?.data?.message ||
                      'Не удалось удалить сотрудника.';
                    dispatch(setErrorNotification(message));
                  }
                }}
                texts={{
                  title: 'Удаление сотрудника',
                  text: `Удалить ${fullName(e)} из компании? Сотрудник вернётся в группу «без компании», его историю заказов/тикетов это не затронет.`,
                  cancel: 'Отмена',
                  confirm: 'Удалить',
                }}
                danger
              />
            )}
          </div>
        );
      },
    },
  ];

  const contactColumns: Column<Contact>[] = [
    {
      key: 'who',
      header: 'Контакт',
      render: (c) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            src={c.photo}
            name={`${c.surname || ''} ${c.name}`.trim()}
            size={36}
            kind="person"
          />
          <div className="min-w-0">
            <p className="font-medium text-[#2d2c2c] truncate">
              {[c.surname, c.name, c.patronymic].filter(Boolean).join(' ')}
            </p>
            <p className="text-xs text-gray-400 truncate">
              {c.position || '—'}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'mail',
      header: 'Email',
      render: (c) =>
        c.mail ? (
          <a
            href={`mailto:${c.mail}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs break-all"
          >
            {c.mail}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'phone',
      header: 'Телефон',
      render: (c) =>
        c.personal_phone ? (
          <a
            href={`tel:${c.personal_phone}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs"
          >
            {c.personal_phone}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      className: 'text-right whitespace-nowrap',
      render: (c) => (
        <div className="flex items-center justify-end gap-3">
          <Can I="change" a="contact">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`contact-person/${c.id}`);
              }}
              className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors"
            >
              Изменить
            </button>
            <ConfirmModal
              openButton={
                <button
                  type="button"
                  className="text-sm font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
                >
                  Удалить
                </button>
              }
              confirm={() => {
                dispatch(deleteAppContact(c.id));
                dispatch(deleteContact(c.id));
              }}
              texts={{
                title: 'Удаление',
                text: `Удалить контакт ${[c.surname, c.name].filter(Boolean).join(' ')}?`,
                cancel: 'Отмена',
                confirm: 'Удалить',
              }}
            />
          </Can>
        </div>
      ),
    },
  ];

  const requestColumns: Column<CompanyRequestInvite>[] = [
    {
      key: 'who',
      header: 'Отправитель',
      render: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-[#2d2c2c] truncate">
            {[r.userSurname, r.userName].filter(Boolean).join(' ') || '—'}
          </p>
          <p className="text-xs text-gray-400">
            {new Date(r.created_at).toLocaleString('ru-RU')}
          </p>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (r) =>
        r.userEmail ? (
          <a
            href={`mailto:${r.userEmail}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs break-all"
          >
            {r.userEmail}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'phone',
      header: 'Телефон',
      render: (r) =>
        r.userPhone ? (
          <a
            href={`tel:${r.userPhone}`}
            onClick={(e) => e.stopPropagation()}
            className="text-gray-600 hover:text-[#e3282a] transition-colors text-xs"
          >
            {r.userPhone}
          </a>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      className: 'text-right whitespace-nowrap',
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await apiClient.post(`/joinrequests/${r.id}/accept/`);
                dispatch(
                  setNotification(
                    `Запрос от ${r.userName} ${r.userSurname} подтверждён`
                  )
                );
                fetchBasicGroupInvites();
                // Сайдбарный бейдж читает счётчик из redux —
                // принудительно обновляем его сразу после accept,
                // иначе цифра будет «жить» до перезагрузки страницы.
                if (companyId) {
                  dispatch(
                    fetchPendingJoinRequestsCount(Number(companyId))
                  );
                }
              } catch (err: any) {
                const message =
                  err?.response?.data?.message ||
                  err?.response?.data?.detail ||
                  'Не удалось принять запрос.';
                dispatch(setErrorNotification(message));
              }
            }}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-[#e3282a] hover:bg-[#c82022] transition-colors"
          >
            Принять
          </button>
          <ConfirmModal
            openButton={
              <button
                type="button"
                className="px-3 py-1.5 rounded-md text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Отклонить
              </button>
            }
            confirm={async () => {
              try {
                await apiClient.delete(`/joinrequests/${r.id}/`);
                dispatch(
                  setNotification(
                    `Запрос от ${r.userName} ${r.userSurname} отклонён`
                  )
                );
                fetchBasicGroupInvites();
                // То же, что и при accept: рефрешим счётчик для бейджа.
                if (companyId) {
                  dispatch(
                    fetchPendingJoinRequestsCount(Number(companyId))
                  );
                }
              } catch (err: any) {
                const message =
                  err?.response?.data?.message ||
                  err?.response?.data?.detail ||
                  'Не удалось отклонить запрос.';
                dispatch(setErrorNotification(message));
              }
            }}
            texts={{
              title: 'Отклонение запроса',
              text: `Отклонить запрос от ${r.userName} ${r.userSurname}?`,
              cancel: 'Отмена',
              confirm: 'Отклонить',
            }}
          />
        </div>
      ),
    },
  ];

  // ─── Обёртка для одной таблицы — карточка с padding'ом ───────────
  const TableCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4">{children}</div>
    </div>
  );

  const FootHelp: React.FC<{ text: React.ReactNode }> = ({ text }) => (
    <p className="text-sm text-gray-500 mt-3">{text}</p>
  );

  return (
    <MainLayout>
      <MainLayout.Title>
        <div style={{ display: 'flex', gap: '20px' }}>
       
          <p>{shouldShowNoCompany ? 'Компания' : `Компания «${company?.name}»`}</p>
        </div>
      </MainLayout.Title>
      <MainLayout.Main>
        {shouldShowNoCompany ? (
        
          <div className="flex flex-col gap-6 w-full">
            <NoCompanyHeroCard
              userPhone={me?.mobile_phone}
              onRequestSent={() =>
                setMyJoinRequestsRefreshKey((k) => k + 1)
              }
            />
         
            <MyJoinRequestsCard refreshKey={myJoinRequestsRefreshKey} />
          </div>
        ) : (
          <>
            {ability.can('view', 'invite') && (
              <CompanyInviteCard
                invite={companyInvite}
                loading={companyInviteLoading}
                onRevoked={fetchCompanyInvite}
              />
            )}
            <Tabs variant="underline" initialActiveTab={activeTab}>
              {ability.can('view', 'legalentity') ? (
                <Tab title={`Юр. лица (${legalEntities.length})`}>
                  <Can I="add" a="legalentity">
                    <div className="mb-3">
                      <AddEntity text={'юр. лицо'} path={'legal-entities/'} />
                    </div>
                  </Can>
                  <TableCard>
                    <DataTable<LegalEntity>
                      columns={legalEntityColumns}
                      data={legalEntities}
                      keyAccessor={(le) => le.id}
                      onRowClick={
                        ability.can('change', 'legalentity')
                          ? (le) => navigate(`legal-entities/${le.id}`)
                          : (le) => setLegalEntityModal(le)
                      }
                      loading={
                        legalEntitiesStatus === 'loading' &&
                        legalEntities.length === 0
                      }
                      emptyMessage="Юр. лиц пока нет"
                      pageSize={DEFAULT_PAGE_SIZE}
                    />
                  </TableCard>
                </Tab>
              ) : (
                <></>
              )}

              {ability.can('viewemployee', 'username') ? (
                <Tab title={`Сотрудники (${filteredEmployees.length})`}>
                  <TableCard>
                    <DataTable<Employee>
                      columns={employeeColumns}
                      data={
                        companyId
                          ? filteredEmployees
                          : employees.filter(
                            (e) => !e.groups?.includes('BASIC')
                          )
                      }
                      keyAccessor={(e) => e.id}
                      onRowClick={(e) => setEmployeeModal(e)}
                      loading={
                        employeesStatus === 'loading' &&
                        filteredEmployees.length === 0
                      }
                      emptyMessage="В компании пока нет сотрудников"
                      pageSize={DEFAULT_PAGE_SIZE}
                    />
                  </TableCard>
                  <FootHelp
                    text={
                      <>
                        Пригласить нового сотрудника можно в разделе{' '}
                        <a
                          href="/settings/users"
                          className="text-[#e3282a] hover:underline font-medium"
                        >
                          «Настройки → Пользователи и роли»
                        </a>
                        .
                      </>
                    }
                  />
                </Tab>
              ) : (
                <></>
              )}

              {ability.can('view', 'contact') ? (
                <Tab title={`Контакты (${contacts?.length || 0})`}>
                  <Can I="add" a="contact">
                    <div className="mb-3">
                      <AddEntity text={'контакт'} path={'contact-person/'} />
                    </div>
                  </Can>
                  <TableCard>
                    <DataTable<Contact>
                      columns={contactColumns}
                      data={contacts || []}
                      keyAccessor={(c) => c.id}
                      onRowClick={(c) => setContactModal(c)}
                      loading={
                        contactsStatus === 'loading' &&
                        (!contacts || contacts.length === 0)
                      }
                      emptyMessage="Контактных лиц пока нет"
                      pageSize={DEFAULT_PAGE_SIZE}
                    />
                  </TableCard>
                </Tab>
              ) : (
                <></>
              )}

              {ability.can('view', 'invite') ? (
                <Tab
                  title="Запросы на присоединение"
                  badge={
                    basicGroupInvites.length > 0
                      ? basicGroupInvites.length
                      : undefined
                  }
                >
                  <TableCard>
                    <DataTable<CompanyRequestInvite>
                      columns={requestColumns}
                      data={basicGroupInvites}
                      keyAccessor={(r) => r.id}
                      emptyMessage="Нет активных запросов на присоединение"
                      pageSize={DEFAULT_PAGE_SIZE}
                    />
                  </TableCard>
                </Tab>
              ) : (
                <></>
              )}
            </Tabs>
          </>
        )}
      </MainLayout.Main>

      {/* Модалки деталей: общий «глаз» из карточек, чтобы не терять
          возможность посмотреть подробности по клику на строку */}
      <MyModal
        isOpen={Boolean(employeeModal)}
        onClose={() => setEmployeeModal(null)}
        title={employeeModal ? fullName(employeeModal) : ''}
        description={employeeModal ? `@${employeeModal.username}` : ''}
        size="md"
      >
        {employeeModal ? <EmployeeDetails employee={employeeModal} /> : null}
      </MyModal>
      <MyModal
        isOpen={Boolean(contactModal)}
        onClose={() => setContactModal(null)}
        title={
          contactModal
            ? [contactModal.surname, contactModal.name, contactModal.patronymic]
              .filter(Boolean)
              .join(' ')
            : ''
        }
        description={contactModal?.position || ''}
        size="md"
      >
        {contactModal ? <ContactDetails contact={contactModal} /> : null}
      </MyModal>
      <MyModal
        isOpen={Boolean(legalEntityModal)}
        onClose={() => setLegalEntityModal(null)}
        title={legalEntityModal?.name || ''}
        description={
          legalEntityModal?.inn
            ? `${getTaxLabelForCompany(company)}: ${legalEntityModal.inn}`
            : ''
        }
        size="md"
      >
        {legalEntityModal ? (
          <LegalEntityDetails legalEntity={legalEntityModal} />
        ) : null}
      </MyModal>
    </MainLayout>
  );
};


const ContactDetails: React.FC<{ contact: Contact }> = ({ contact }) => (
  <dl className="text-sm space-y-2">
    {contact.mail && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Email</dt>
        <dd className="text-[#2d2c2c] break-all text-right">
          <a
            href={`mailto:${contact.mail}`}
            className="hover:text-[#e3282a] transition-colors"
          >
            {contact.mail}
          </a>
        </dd>
      </div>
    )}
    {contact.personal_phone && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Телефон</dt>
        <dd className="text-[#2d2c2c]">
          <a
            href={`tel:${contact.personal_phone}`}
            className="hover:text-[#e3282a] transition-colors"
          >
            {contact.personal_phone}
          </a>
        </dd>
      </div>
    )}
    {contact.telegram && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Telegram</dt>
        <dd className="text-[#2d2c2c]">{contact.telegram}</dd>
      </div>
    )}
    {contact.whatsapp && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">WhatsApp</dt>
        <dd className="text-[#2d2c2c]">{contact.whatsapp}</dd>
      </div>
    )}
  </dl>
);

const EmployeeDetails: React.FC<{ employee: Employee }> = ({ employee }) => (
  <dl className="text-sm space-y-2">
    {employee.email && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Email</dt>
        <dd className="text-[#2d2c2c] break-all text-right">
          <a
            href={`mailto:${employee.email}`}
            className="hover:text-[#e3282a] transition-colors"
          >
            {employee.email}
          </a>
        </dd>
      </div>
    )}
    {employee.mobile_phone && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Телефон</dt>
        <dd className="text-[#2d2c2c]">
          <a
            href={`tel:${employee.mobile_phone}`}
            className="hover:text-[#e3282a] transition-colors"
          >
            {employee.mobile_phone}
          </a>
        </dd>
      </div>
    )}
    {employee.telegram && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Telegram</dt>
        <dd className="text-[#2d2c2c]">{employee.telegram}</dd>
      </div>
    )}
    {employee.whatsapp && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">WhatsApp</dt>
        <dd className="text-[#2d2c2c]">{employee.whatsapp}</dd>
      </div>
    )}
  </dl>
);

// View-only-карточка юр. лица. ИНН/БИН отдаём в подзаголовок самой
// модалки (см. <MyModal description={...}>), здесь только email,
// телефоны и адрес — те же поля, что показываются в колонках таблицы.
const LegalEntityDetails: React.FC<{ legalEntity: LegalEntity }> = ({
  legalEntity,
}) => (
  <dl className="text-sm space-y-2">
    {legalEntity.email && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">Email</dt>
        <dd className="text-[#2d2c2c] break-all text-right">
          <a
            href={`mailto:${legalEntity.email}`}
            className="hover:text-[#e3282a] transition-colors"
          >
            {legalEntity.email}
          </a>
        </dd>
      </div>
    )}
    {legalEntity.phones && legalEntity.phones.length > 0 && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400">
          {legalEntity.phones.length > 1 ? 'Телефоны' : 'Телефон'}
        </dt>
        <dd className="text-[#2d2c2c] text-right">
          <div className="flex flex-col items-end gap-1">
            {legalEntity.phones.map((phone) => (
              <a
                key={phone}
                href={`tel:${phone}`}
                className="hover:text-[#e3282a] transition-colors"
              >
                {phone}
              </a>
            ))}
          </div>
        </dd>
      </div>
    )}
    {legalEntity.addresses && legalEntity.addresses.length > 0 && (
      <div className="flex justify-between gap-4">
        <dt className="text-gray-400 shrink-0">
          {legalEntity.addresses.length > 1 ? 'Адреса' : 'Адрес'}
        </dt>
        <dd className="text-[#2d2c2c] text-right">
          <div className="flex flex-col items-end gap-1">
            {legalEntity.addresses.map((address, idx) => {
              const line = Object.values(address).filter(Boolean).join(', ');
              return line ? <div key={idx}>{line}</div> : null;
            })}
          </div>
        </dd>
      </div>
    )}
  </dl>
);

/* ─────────────── Карточка «Ссылка для регистрации» ─────────────── */

// Показывает текущий «боевой» инвайт компании (тот, что 1С создаёт
// при выгрузке). Цель карточки — дать менеджеру быстро скопировать
// ссылку и отправить будущему оунеру, не уходя в Настройки.
//
// Состояния:
//   - идёт загрузка → скелет;
//   - инвайта нет / он использован → информативная подсказка;
//   - есть unused инвайт → ссылка, кнопки «Скопировать» / «Отозвать».

interface CompanyInviteCardProps {
  invite: InviteWithMeta | null;
  loading: boolean;
  onRevoked: () => void;
}

const CompanyInviteCard: React.FC<CompanyInviteCardProps> = ({
  invite,
  loading,
  onRevoked,
}) => {
  const dispatch = useAppDispatch();

  const handleCopy = async (link: string) => {
    const ok = await copyToClipboard(link);
    dispatch(
      ok
        ? setNotification('Ссылка скопирована в буфер обмена')
        : setErrorNotification('Не удалось скопировать ссылку')
    );
  };

  const handleRevoke = async () => {
    if (!invite) return;
    try {
      await rolesApi.revokeInvite(invite.id);
      dispatch(setNotification('Приглашение отозвано'));
      onRevoked();
    } catch {
      dispatch(setErrorNotification('Не удалось отозвать приглашение'));
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm px-6 py-4 mb-4 animate-pulse">
        <div className="h-3 w-40 bg-gray-200 rounded mb-3" />
        <div className="h-9 w-full bg-gray-100 rounded" />
      </div>
    );
  }

  // Нет инвайта — карточку не показываем вовсе, чтобы не плодить
  // пустые блоки на странице компании.
  if (!invite) {
    return null;
  }

  const link = buildInviteLink(invite.id);
  const status = INVITE_STATUS_STYLE[invite.status] || INVITE_STATUS_STYLE.unused;
  const isActive = invite.status === 'unused';

  return (
    <div className="bg-white rounded-xl shadow-sm px-6 py-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-[#2d2c2c]">
          Ссылка для регистрации
        </p>
        <span
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          style={{ background: status.bg, color: status.fg }}
        >
          {status.label}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <input
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-600 font-mono truncate focus:outline-none focus:ring-2 focus:ring-red-300"
        />
        <Button
          isWhite
          onClick={() => {
            void handleCopy(link);
          }}
          disabled={!isActive}
        >
          Скопировать
        </Button>
        {isActive && (
          <ConfirmModal
            danger
            openButton={
              <button
                type="button"
                className="text-sm font-medium text-[#e3282a] hover:text-[#c82022] transition-colors px-2"
              >
                Отозвать
              </button>
            }
            confirm={() => {
              void handleRevoke();
            }}
            texts={{
              title: 'Отозвать приглашение?',
              text: 'После этого ссылка перестанет работать. Получивший её человек не сможет зарегистрироваться.',
              cancel: 'Отмена',
              confirm: 'Отозвать',
            }}
          />
        )}
      </div>

      <p className="text-xs text-gray-400 mt-2">
        {invite.status === 'used'
          ? 'Ссылка уже использована — компания зарегистрирована.'
          : invite.status === 'blocked'
            ? 'Ссылка отозвана. Чтобы выпустить новую — повторите выгрузку из 1С.'
            : 'Отправьте ссылку будущему владельцу компании. После регистрации статус сменится на «Использовано».'}
        {invite.created_at
          ? ` · Создан ${new Date(invite.created_at).toLocaleString('ru-RU')}`
          : ''}
      </p>
    </div>
  );
};

/* ─────────────── Карточка-«заглушка» для нулевого юзера ─────────────── */

// Юзер без компании на /company раньше видел просто центрированный
// текст и маленькую кнопку «+ Добавить компанию». На общем фоне
// (Profile/Cart/Orders все используют bg-white rounded-xl shadow-sm
// карточки) это смотрелось как 404, а не как полноценный экран.
// Здесь — стандартная карточка с иконкой, заголовком, описанием
// и CTA. Стиль (rounded-xl, shadow-sm, px/py) совпадает с другими
// карточками на странице (CompanyInviteCard, TableCard, MyJoinRequestsCard),
// чтобы у пустого состояния и наполненного был одинаковый ритм.
const NoCompanyHeroCard: React.FC<{
  userPhone?: string;
  /**
   * Прокидываем дальше в `CompanyAccessRequest`. Используется,
   * чтобы после успешной отправки заявки соседняя карточка
   * `MyJoinRequestsCard` рефрешнулась без перезагрузки страницы.
   */
  onRequestSent?: () => void;
}> = ({ userPhone, onRequestSent }) => (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
    {/* Левое выравнивание — как у остальных карточек на странице
        (CompanyInviteCard, TableCard, MyJoinRequestsCard). Раньше
        контент был отцентрован «лендинговым» способом, и нулевой
        юзер видел один экран, который не похож на «полный»
        вариант — теперь визуальный ритм одинаковый. */}
    <div className="px-6 py-5">
      <div className="flex items-start gap-4">
        {/* Иконка-кружок: красная заливка чуть прозрачная, чтобы
            совпадать с акцентным красным #e3282a, который везде в
            проекте используется для primary-действий. */}
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
          style={{ background: '#fde7e7' }}
          aria-hidden="true"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 21V7a2 2 0 0 1 2-2h7v16M14 21v-9h5a2 2 0 0 1 2 2v7M7 9h2M7 13h2M7 17h2M16 16h2M16 19h2"
              stroke="#e3282a"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-[#2d2c2c]">
            У вас пока нет доступа к компании
          </h3>
          <p className="text-sm text-gray-500 mt-1 max-w-[640px]">
            Здесь Вы можете отправить запрос на регистрацию своей компании,
            или на присоединение к существующей. В случае регистрации своей
            компании, с Вами свяжется менеджер для обсуждения дальнейших
            действий. В случае присоединения к существующей компании,
            подтверждает запрос пользователь этой компании с соответствующим
            правом.
          </p>
          <div className="mt-4">
            {/* CompanyAccessRequest рендерит триггер-«+ Добавить
                компанию» вместе с модалкой формы. Не оборачиваем в
                дополнительный Button, чтобы стиль остался такой же,
                как у «+ Добавить юр.лицо» / «+ Добавить контакт» в
                полной версии страницы. */}
            <CompanyAccessRequest
              userPhone={userPhone}
              onSuccess={onRequestSent}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ─────────────── Карточка «Мои запросы и приглашения» ─────────────── */

// Показывается на странице /company у юзера без компании. Отдельный
// компонент, чтобы не загромождать основной Company-рендер: эту
// часть видит ровно подмножество BASIC-юзеров, и на остальных
// сценариях /company она вообще не маунтится.
//
// Источник данных — endpoint /joinrequests/mine/, который отдаёт
// и исходящие (что юзер сам отправил), и входящие (когда компания
// пригласила его). PartOfCompanyFilter стандартный list бы зарезал
// — у нулевого юзера company=null. `mine/` его обходит.
//
// UI — таблица DataTable с колонками: компания, тип, статус, дата.
// Для PENDING + OUTGOING показываем «Отменить» (вызывает /cancel/).
// Для ACCEPTED — ссылка на /company/{id} (но формально на этом
// экране ACCEPTED появляется редко: как только заявка принята,
// у юзера уже есть company, и shouldShowNoCompany=false; всё-таки
// рендерим, потому что между accept и /me-rerender может быть
// короткое окно, и для надёжности пусть будет ссылка).

const STATUS_BADGE: Record<
  CompanyRequestInvite['status'],
  { label: string; bg: string; fg: string }
> = {
  pending: { label: 'Отправлен', bg: '#fef3c7', fg: '#92400e' },
  accepted: { label: 'Принят', bg: '#d1fae5', fg: '#065f46' },
  rejected: { label: 'Отклонён', bg: '#fee2e2', fg: '#991b1b' },
  cancelled: { label: 'Отозван', bg: '#e5e7eb', fg: '#4b5563' },
};

const DIRECTION_LABEL: Record<
  CompanyRequestInvite['direction'],
  string
> = {
  outgoing: 'Исходящий',
  incoming: 'Входящий',
};

interface MyJoinRequestsCardProps {
  /**
   * Триггер ререфетча. Любое изменение значения вызывает повторный
   * GET /joinrequests/mine/. Нужен, чтобы после успешного submit в
   * соседней `CompanyAccessRequest` список тут же обновился без F5.
   * Дефолт 0 — компонент работает и без пропа (mount-only fetch).
   */
  refreshKey?: number;
}

const MyJoinRequestsCard: React.FC<MyJoinRequestsCardProps> = ({
  refreshKey = 0,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [items, setItems] = useState<CompanyRequestInvite[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMine = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/joinrequests/mine/');
      // На бэке отдаётся уже отсортировано по -created_at, но на
      // случай прокси-кэша/старой версии всё равно сортируем.
      const data: CompanyRequestInvite[] = Array.isArray(response.data)
        ? response.data
        : [];
      data.sort(
        (a, b) =>
          new Date(b.created_at).getTime() -
          new Date(a.created_at).getTime()
      );
      setItems(data);
    } catch (error) {
      console.error('Ошибка при загрузке моих запросов:', error);
      // Молча: если сервер ещё без /mine/, у юзера просто будет
      // пустой список — это безопаснее, чем красное уведомление.
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Перетягиваем список и при mount, и каждый раз, когда родитель
  // бумпает `refreshKey` (после отправки новой заявки в соседней
  // форме). Пустой массив тут был бы багом — список оставался стейл.
  useEffect(() => {
    fetchMine();
  }, [refreshKey]);

  const handleCancel = async (id: string) => {
    try {
      await apiClient.post(`/joinrequests/${id}/cancel/`);
      dispatch(setNotification('Запрос отозван'));
      fetchMine();
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Не удалось отозвать запрос.';
      dispatch(setErrorNotification(message));
    }
  };

  const myRequestColumns: Column<CompanyRequestInvite>[] = [
    {
      key: 'company',
      header: 'Компания',
      render: (r) => (
        <div className="min-w-0">
          {r.status === 'accepted' && r.companyId ? (
            // ACCEPTED — пользователь уже принят, и у него теперь
            // есть права на /company/{id}. Делаем строку кликабельной.
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/company/${r.companyId}`);
              }}
              className="font-medium text-[#e3282a] hover:text-[#c82022] transition-colors text-left truncate"
            >
              {r.companyName || `Компания #${r.companyId}`}
            </button>
          ) : (
            <p
              className="font-medium text-[#2d2c2c] truncate"
              title={r.companyName || ''}
            >
              {r.companyName || '—'}
            </p>
          )}
          <p className="text-xs text-gray-400">
            {new Date(r.created_at).toLocaleString('ru-RU')}
          </p>
        </div>
      ),
    },
    {
      key: 'direction',
      header: 'Тип',
      render: (r) => (
        <span className="text-xs text-gray-600">
          {DIRECTION_LABEL[r.direction] || r.direction}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      render: (r) => {
        const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
        return (
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ background: badge.bg, color: badge.fg }}
          >
            {badge.label}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      stopRowClick: true,
      className: 'text-right whitespace-nowrap',
      render: (r) => {
        // Кнопки нужны только в активном PENDING-состоянии. По
        // принятым/отклонённым/отозванным юзер уже ничего сделать
        // не может — это история.
        if (r.status !== 'pending') return null;
        if (r.direction === 'outgoing') {
          return (
            <ConfirmModal
              openButton={
                <button
                  type="button"
                  className="text-sm font-medium text-gray-500 hover:text-[#e3282a] transition-colors"
                >
                  Отозвать
                </button>
              }
              confirm={() => {
                void handleCancel(r.id);
              }}
              texts={{
                title: 'Отозвать запрос?',
                text: `Запрос в «${r.companyName || 'компанию'}» больше не будет виден её менеджерам.`,
                cancel: 'Отмена',
                confirm: 'Отозвать',
              }}
            />
          );
        }
        // INCOMING-приглашения от компании: бэкенд-флоу принятия/
        // отклонения существующим юзером ещё не реализован, поэтому
        // пока действия не показываем. Карточка останется в UI —
        // юзер увидит, что приглашение есть; кнопки добавим в
        // следующей итерации, когда появится `respond_invite`.
        return null;
      },
    },
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <h4 className="text-sm font-semibold text-[#2d2c2c]">
          Мои запросы и приглашения
        </h4>
        <p className="text-xs text-gray-400 mt-1">
          Здесь видны заявки, которые вы отправили в компании, и
          приглашения, присланные вам.
        </p>
      </div>
      <div className="px-6 py-4">
        <DataTable<CompanyRequestInvite>
          columns={myRequestColumns}
          data={items}
          keyAccessor={(r) => r.id}
          loading={loading && items.length === 0}
          emptyMessage="Запросов и приглашений пока нет"
          pageSize={DEFAULT_PAGE_SIZE}
        />
      </div>
    </div>
  );
};

export default Company;
